import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {apiClient} from '@/shared/services';
import {
    PatientDashboardModuleKeyById,
    type PatientDashboardModuleKey,
} from '@mld/shared/constants/dashboardModuleAccess';

/**
 * Effective patient-dashboard module flags for the logged-in user, as resolved
 * by `GET /api/user/dashboard-config` (org AND provider, both default-on).
 *
 * The switches behind this live in Super Admin → Organizations → Modules
 * ("Patient Dashboard Modules" org-wide, "Patient Dashboard Access" per
 * provider), so which cards an org sees is an admin decision, not a rule in
 * the code.
 *
 * Returns `null` while the config has not loaded — meaning "show everything",
 * so a slow or failed request never blanks the dashboard.
 */
export function useDashboardModules(): Set<PatientDashboardModuleKey> | null {
    const {user} = useAuth();
    const {data} = useQuery<{modules: Array<{module_id: number; enabled: boolean}>}>({
        queryKey: ['dashboard-config'],
        queryFn: () => apiClient.get('/api/user/dashboard-config'),
        enabled: !!user?.isLoggedIn,
        staleTime: 5 * 60 * 1000,
    });

    return useMemo(() => {
        const enabledSet = new Set<PatientDashboardModuleKey>();
        if (!data?.modules) {
            // Default: all enabled
            return null;
        }
        data.modules.forEach((m) => {
            if (m.enabled) {
                const key = PatientDashboardModuleKeyById[m.module_id as keyof typeof PatientDashboardModuleKeyById];
                if (key) enabledSet.add(key);
            }
        });
        return enabledSet;
    }, [data]);
}

/**
 * Predicate form of {@link useDashboardModules}. Keeps the "not loaded yet ⇒
 * enabled" rule in one place rather than re-deriving it at every call site.
 */
export function useIsDashboardModuleEnabled(): (key: PatientDashboardModuleKey) => boolean {
    const enabledModules = useDashboardModules();
    return (key: PatientDashboardModuleKey) => !enabledModules || enabledModules.has(key);
}
